import { isAfter, parseISO, subHours } from "date-fns";

import analytics from "../lib/analytics";
import { ICartProductDetail } from "../types/cart";

export interface IProductAnalytics {
  brand?: string | null;
  category?: string | null;
  dimension1?: string | null;
  dimension2?: string | null;
  dimension8?: string | null;
  id?: string | null;
  ean?: string | null;
  sku?: string | null;
  name?: string | null;
  price?: string | null;
  quantity?: number | null;
  variant?: string | null;
}

export const isNewClient = ({
  isAuthenticated,
  createdAt,
}: {
  createdAt?: string;
  isAuthenticated?: boolean;
}): boolean => {
  if (!isAuthenticated || !createdAt) {
    return true;
  }

  const yesterday = subHours(new Date(), 24);

  return isAfter(parseISO(createdAt), yesterday);
};

export const getLengowQuoteItems = (
  items: ICartProductDetail[]
): string | undefined => {
  if (!items || items.length === 0) {
    return undefined;
  }

  return items.reduce(
    (
      accumulator: string,
      currentValue: ICartProductDetail,
      index: number
    ): string =>
      `${accumulator && accumulator + ","}i{${index + 1}}={${
        currentValue.product.sku
      }}&p{${index + 1}}={${currentValue.product.actualPrice}}&q{${
        index + 1
      }}={${currentValue.quantity}}`,
    ""
  );
};

export const getSkuList = (items: ICartProductDetail[]): string | undefined => {
  if (!items || items.length === 0) {
    return undefined;
  }

  return items.reduce(
    (accumulator: string, currentValue: ICartProductDetail): string =>
      `${accumulator && accumulator + ","}${currentValue.product.sku}`,
    ""
  );
};

export const pushUserActionTracking = ({
  customCategorie,
  customAction,
  customLabel,
}: {
  customAction: string;
  customCategorie: string;
  customLabel: string | boolean | string[];
}): void => {
  analytics.push({
    customAction,
    customCategorie,
    customLabel,
    event: "customEvent",
  });
};

export const pushAddToCartTracking = (productName: string): void => {
  pushUserActionTracking({
    customAction: "ajoutAuPanier",
    customCategorie: "engagement",
    customLabel: productName,
  });
};

export const pushRemoveFromCartTracking = (productName: string): void => {
  pushUserActionTracking({
    customAction: "retraitDuPanier",
    customCategorie: "engagement",
    customLabel: productName,
  });
};

export const subscribeToAlertTracking = (
  alertType: "newsletter" | "alerteStock" | "alerteNouveautes"
): void => {
  pushUserActionTracking({
    customAction: "inscription",
    customCategorie: "engagement",
    customLabel: alertType,
  });
};

export const onClickMenuElementLinkTracking = (
  MenuElementLink: string
): void => {
  pushUserActionTracking({
    customAction: "menu",
    customCategorie: "navigation",
    customLabel: MenuElementLink,
  });
};

export const filterResultsTracking = (filter: string[]): void => {
  pushUserActionTracking({
    customAction: "filtres",
    customCategorie: "navigation",
    customLabel: filter,
  });
};

export const deliveryModeTracking = (deliveryMode: string): void => {
  pushUserActionTracking({
    customAction: "modeLivraison",
    customCategorie: "conversion",
    customLabel: deliveryMode,
  });
};
export const paymentMethodTracking = (paymentMethod: string): void => {
  pushUserActionTracking({
    customAction: "modePaiement",
    customCategorie: "conversion",
    customLabel: paymentMethod,
  });
};
export const footerInteractionTracking = (footerInteraction: string): void => {
  pushUserActionTracking({
    customAction: footerInteraction,
    customCategorie: "footer",
    customLabel: footerInteraction,
  });
};
export const discountValidationTracking = (discount: string): void => {
  pushUserActionTracking({
    customAction: "codeAvantage",
    customCategorie: "conversion",
    customLabel: discount,
  });
};
export const bannerHomeOnClickTracking = (banner: string): void => {
  pushUserActionTracking({
    customAction: banner,
    customCategorie: banner,
    customLabel: banner,
  });
};
export const accountCreatedTracking = (newAccount: boolean): void => {
  pushUserActionTracking({
    customAction: "creationCompte",
    customCategorie: "engagement",
    customLabel: newAccount,
  });
};
export const addToWishListTracking = (newProductWished: string): void => {
  pushUserActionTracking({
    customAction: "ajoutAuxPreferes",
    customCategorie: "engagement",
    customLabel: newProductWished,
  });
};
